import { useCallback, useState } from "react"
import { Badge } from "@ui/badge"
import { Input } from "@ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/select"
import { Skeleton } from "@ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/table"
import { Ban, Search } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { useContactSearch, useOrganizations } from "../hooks/use-conversation-timeline"
import type { ContactSearchResult } from "../types/conversation-timeline"
import { AdminPageContainer } from "./admin-page-container"

function useDebounce(delay: number) {
  const [value, setValue] = useState("")
  const [debouncedValue, setDebouncedValue] = useState("")

  const onChange = useCallback(
    (newValue: string) => {
      setValue(newValue)
      const handler = setTimeout(() => setDebouncedValue(newValue), delay)
      return () => clearTimeout(handler)
    },
    [delay]
  )

  return { value, debouncedValue, onChange }
}

export function ContactSearch() {
  const navigate = useNavigate()
  const { data: organizations } = useOrganizations()
  const [selectedOrg, setSelectedOrg] = useState<string>("")
  const { value: searchInput, debouncedValue: searchQuery, onChange: setSearch } = useDebounce(300)

  const orgFilter = selectedOrg && selectedOrg !== "all" ? selectedOrg : undefined
  const { data: searchResult, isLoading } = useContactSearch(searchQuery, orgFilter)

  const handleRowClick = (contact: ContactSearchResult) => {
    navigate(`/admin/conversations/contact/${contact.id}`)
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString("pt-BR")
  }

  return (
    <AdminPageContainer title="Conversas" subtitle="Buscar contatos e ver timeline de conversas">
      <div className="flex flex-col gap-4 sm:flex-row">
        <Select value={selectedOrg} onValueChange={setSelectedOrg}>
          <SelectTrigger className="w-full sm:w-[320px]">
            <SelectValue placeholder="Todas as organizações" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as organizações</SelectItem>
            {organizations?.map(org => (
              <SelectItem key={org.id} value={org.id}>
                {org.name} - {org.owner_email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Buscar por telefone ou nome..."
            value={searchInput}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {searchResult && searchResult.items.length > 0 && (
        <>
          <p className="text-muted-foreground text-sm">
            {searchResult.total} resultado{searchResult.total !== 1 ? "s" : ""}
          </p>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Organização</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {searchResult.items.map(contact => (
                  <TableRow
                    key={contact.id}
                    className="cursor-pointer"
                    onClick={() => handleRowClick(contact)}
                  >
                    <TableCell>{contact.name || contact.profile_name || "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{contact.phone_number}</TableCell>
                    <TableCell>
                      <div className="text-sm">{contact.organization_name || "—"}</div>
                      {contact.owner_email && (
                        <div className="text-muted-foreground text-xs">{contact.owner_email}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.is_blocked ? (
                        <Badge variant="destructive" className="text-xs">
                          <Ban className="mr-1 h-3 w-3" />
                          Bloqueado
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          Ativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(contact.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {searchResult && searchResult.items.length === 0 && searchQuery.length >= 2 && (
        <div className="text-muted-foreground py-12 text-center">
          Nenhum contato encontrado para "{searchQuery}"
        </div>
      )}

      {!searchResult && !isLoading && searchQuery.length < 2 && (
        <div className="text-muted-foreground py-12 text-center">
          Digite ao menos 2 caracteres para buscar contatos
        </div>
      )}
    </AdminPageContainer>
  )
}
